import { create, isAxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { env } from '@/config/env';

import { toApiError } from './errors';
import { tokenStore } from './token-store';
import type { ApiEnvelope, Page, PaginatedEnvelope } from './types';

/**
 * The single HTTP client for the admin.
 *
 * Nothing outside this module may import `axios`. Everything a request needs to
 * be correct — base URL, bearer token, JSON negotiation, envelope unwrapping,
 * error normalisation, session expiry handling — happens here exactly once, so a
 * new feature cannot forget any of it.
 */

/** Fired when the API rejects the current token. The router listens and redirects. */
export const SESSION_EXPIRED_EVENT = 'dp:session-expired';

const instance: AxiosInstance = create({
  baseURL: `${env.apiBaseUrl}/api/v1`,
  timeout: env.requestTimeoutMs,
  headers: {
    Accept: 'application/json',
    // Laravel only takes the JSON path for exception rendering when the request
    // looks like an API call. Without this a 419 or 500 can come back as HTML.
    'X-Requested-With': 'XMLHttpRequest',
    //
    // `Content-Type` is deliberately absent. Axios derives it from the body:
    // `application/json` for a plain object, `multipart/form-data` with a
    // generated boundary for `FormData`. Pinning it to JSON here would break
    // every upload, and the usual workaround — deleting the header per request —
    // fails silently when the default is merged back in.
  },
});

instance.interceptors.request.use((config) => {
  const token = tokenStore.get();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error);

    // A 401 means the token is gone or expired. Drop it so the next render sees
    // a logged-out app rather than retrying with a credential that will never
    // work again. The login request itself is exempt: a wrong password is a
    // form error, not an expired session.
    const requestUrl = isAxiosError(error) ? (error.config?.url ?? '') : '';
    const isLoginAttempt = requestUrl.includes('/admin/login');

    if (apiError.isUnauthenticated && !isLoginAttempt && tokenStore.get() !== null) {
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    return Promise.reject(apiError);
  },
);

/** Query values that survive a round trip through `URLSearchParams`. */
export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | (string | number)[]
>;

function serialiseParams(params: QueryParams): URLSearchParams {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      // Laravel reads `filter[]=a&filter[]=b` into an array; a comma-joined
      // string would arrive as one value containing a comma.
      for (const entry of value) {
        search.append(`${key}[]`, String(entry));
      }
      continue;
    }

    search.append(key, String(value));
  }

  return search;
}

interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  /** Override the default axios timeout for long-running admin jobs (import). */
  timeout?: number;
}

function configFor(options: RequestOptions | undefined): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};

  if (options?.params) {
    config.params = serialiseParams(options.params);
  }

  if (options?.signal) {
    config.signal = options.signal;
  }

  if (options?.timeout !== undefined) {
    config.timeout = options.timeout;
  }

  return config;
}

export const http = {
  /** GET returning the unwrapped `data` of the envelope. */
  async get<TData>(path: string, options?: RequestOptions): Promise<TData> {
    const response = await instance.get<ApiEnvelope<TData>>(path, configFor(options));
    return response.data.data;
  },

  /** GET returning items plus pagination metadata. */
  async getPage<TItem>(path: string, options?: RequestOptions): Promise<Page<TItem>> {
    const response = await instance.get<PaginatedEnvelope<TItem>>(path, configFor(options));
    return { items: response.data.data, meta: response.data.meta };
  },

  /** GET returning the whole envelope, for the rare caller that needs `meta`. */
  async getEnvelope<TData>(path: string, options?: RequestOptions): Promise<ApiEnvelope<TData>> {
    const response = await instance.get<ApiEnvelope<TData>>(path, configFor(options));
    return response.data;
  },

  async post<TData>(path: string, body?: unknown, options?: RequestOptions): Promise<TData> {
    const response = await instance.post<ApiEnvelope<TData>>(path, body, configFor(options));
    return response.data.data;
  },

  async put<TData>(path: string, body?: unknown, options?: RequestOptions): Promise<TData> {
    const response = await instance.put<ApiEnvelope<TData>>(path, body, configFor(options));
    return response.data.data;
  },

  async patch<TData>(path: string, body?: unknown, options?: RequestOptions): Promise<TData> {
    const response = await instance.patch<ApiEnvelope<TData>>(path, body, configFor(options));
    return response.data.data;
  },

  /** DELETE. The API answers 204 with no body, so there is nothing to return. */
  async delete(path: string, options?: RequestOptions): Promise<void> {
    await instance.delete(path, configFor(options));
  },

  /** Multipart upload with progress, for the media library. */
  async upload<TData>(
    path: string,
    formData: FormData,
    onProgress?: (percent: number) => void,
  ): Promise<TData> {
    const response = await instance.post<ApiEnvelope<TData>>(path, formData, {
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });

    return response.data.data;
  },
};

/** Exposed for tests and interceptor wiring only. */
export const httpInstance = instance;
